import { MagnifyingGlass } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { request } from '../api/client'
import type { EmployeeOption, PageData } from '../api/types'

const EMPLOYEE_PAGE_SIZE = 200

export interface EmployeeFilterSpecialOption {
  key: string
  label: string
  note: string
  selected: boolean
  onToggle: () => void
}

interface EmployeeFilterPickerProps {
  values: string[]
  onChange: (values: string[]) => void
  specialOptions?: EmployeeFilterSpecialOption[]
}

async function loadActiveEmployees() {
  const first = await request<PageData<EmployeeOption>>({
    url: '/luxury/product/catalog/employee/page',
    params: { pageNo: 1, pageSize: EMPLOYEE_PAGE_SIZE, status: 0 },
  })
  const employees = [...first.list]
  const pageCount = Math.ceil(first.total / EMPLOYEE_PAGE_SIZE)
  for (let pageNo = 2; pageNo <= pageCount; pageNo += 1) {
    const page = await request<PageData<EmployeeOption>>({
      url: '/luxury/product/catalog/employee/page',
      params: { pageNo, pageSize: EMPLOYEE_PAGE_SIZE, status: 0 },
    })
    employees.push(...page.list)
  }
  return [...new Map(employees.map((employee) => [String(employee.id), employee])).values()]
}

function normalize(value: string) {
  return value.trim().toLocaleLowerCase('zh-CN')
}

function storeNames(employee: EmployeeOption) {
  return employee.stores?.map((store) => store.name.trim()).filter(Boolean) || []
}

function groupName(employee: EmployeeOption) {
  const stores = employee.stores || []
  return stores.find((store) => store.primary)?.name.trim()
    || stores[0]?.name.trim()
    || '未分配门店'
}

function toggle(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

export function EmployeeFilterPicker({ values, onChange, specialOptions = [] }: EmployeeFilterPickerProps) {
  const [keyword, setKeyword] = useState('')
  const employees = useQuery({
    queryKey: ['employee-filter-options'],
    queryFn: loadActiveEmployees,
    staleTime: 60_000,
  })
  const groups = useMemo(() => {
    const query = normalize(keyword)
    const result = new Map<string, EmployeeOption[]>()
    for (const employee of employees.data || []) {
      const searchable = normalize([employee.name, employee.roleName, ...storeNames(employee)].filter(Boolean).join(' '))
      if (query && !searchable.includes(query)) continue
      const store = groupName(employee)
      result.set(store, [...(result.get(store) || []), employee])
    }
    return result
  }, [employees.data, keyword])
  const query = normalize(keyword)
  const visibleSpecials = specialOptions.filter((option) => !query || normalize(`${option.label} ${option.note}`).includes(query))
  const empty = !employees.isLoading && !employees.isError && groups.size === 0 && visibleSpecials.length === 0

  return <fieldset className="advanced-filter-section employee-picker-section employee-filter-picker">
    <legend>选择员工</legend>
    <div className="employee-filter-picker-shell">
      <label className="picker-search employee-filter-search"><MagnifyingGlass /><input aria-label="搜索员工" autoFocus value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜索姓名、门店或角色" /></label>
      <div className="employee-directory employee-filter-directory" aria-label="员工列表">
        {employees.isLoading && <div className="picker-empty employee-filter-state">正在加载员工</div>}
        {employees.isError && <div className="picker-empty employee-filter-state"><span>员工加载失败</span><button type="button" onClick={() => employees.refetch()}>重新加载</button></div>}
        {[...groups.entries()].map(([store, items]) => <section className="employee-filter-group" key={store}><div className="employee-group-head"><strong>{store}</strong><span>{items.length} 人</span></div>{items.map((employee) => {
          const id = String(employee.id)
          const stores = storeNames(employee)
          const note = [employee.roleName, stores.join('、')].filter(Boolean).join(' · ')
          return <label className="employee-row employee-filter-option" key={id}><span className="employee-avatar">{employee.name.trim().slice(0, 1) || '?'}</span><span className="employee-copy"><strong>{employee.name}</strong><small>{note || '员工'}</small></span><input type="checkbox" checked={values.includes(id)} onChange={() => onChange(toggle(values, id))} /></label>
        })}</section>)}
        {visibleSpecials.length > 0 && <section className="employee-filter-group"><div className="employee-group-head"><strong>其他</strong><span>状态项</span></div>{visibleSpecials.map((option) => <label className="employee-row employee-filter-option employee-special" key={option.key}><span className="employee-avatar">—</span><span className="employee-copy"><strong>{option.label}</strong><small>{option.note}</small></span><input type="checkbox" checked={option.selected} onChange={option.onToggle} /></label>)}</section>}
        {empty && <div className="picker-empty employee-filter-state">没有找到匹配的员工</div>}
      </div>
    </div>
  </fieldset>
}
